import { DictType } from '@pma/types';
import { useEffect, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/common/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/common/popover';
import { cn } from '@pma/utils/helpers';
import { Button } from '@components/common/button';
import axios from 'axios';
import { parseCookies } from 'nookies';
import { ACCESS_TOKEN } from '@pma/utils/constants';
import { useAuth } from '@stores/auth-store';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes';
import useTranslator from '@hooks/use-translator';
import { editProjectByCurrentUser } from '@pma/utils/utils';
import AddWorkTypeForm from './add-work-type-form';
import { Separator } from '@components/common/separator';
import ProjectWorkTypeDictionaryEditor from './project-work-type-dictionary-editor';

interface Props {
  workType?: DictType;
  projectId?: number;
  setWorkType?: any;
}
const ProjectWorkTypeEditor = ({ workType, projectId, setWorkType }: Props) => {
  const [open, setOpen] = useState(false);

  const { authConfig } = useAuth();
  const navigate = useNavigate();
  const { translate, locale } = useTranslator();
  const location = useLocation();

  const isHebrew = locale === 'he';

  const [value, setValue] = useState(
    isHebrew ? (workType?.he ? workType?.he : workType?.en) : workType?.en,
  );
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;

  const searchParams = location.search;

  const getWorkTypes = async () => {
    const response = await axios.get(
      SERVER_URL + PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT,
      {
        params: {
          organization_id: orgId,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };
  const { data: workTypes, refetch: refetchWorkTypes } = useQuery({
    queryKey: ['work-types', orgId],
    queryFn: getWorkTypes,
  });

  const updateProjectWorkTypeMutation = useMutation({
    mutationKey: ['update-project-work-type'],
    mutationFn: (workTypeId: number) => {
      return editProjectByCurrentUser({
        project_id: projectId || 0,
        work_type_id: workTypeId,
        inform_users: true,
      });
    },
    onSuccess: () => {
      // Trigger Revalidate project table
      navigate(
        searchParams
          ? `${searchParams}&revalidate=projects`
          : `?revalidate=projects`,
      );
    },
  });

  useEffect(() => {
    workType && setValue(workType?.id);
  }, [workType]);

  if (projectId) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger>
          <Button
            variant='outline'
            role='combobox'
            className='justify-start p-1 text-black w-full outline-black outline outline-1 bg-transparent h-6 capitalize'
          >
            {value
              ? isHebrew
                ? workTypes?.find((workType: DictType) => workType?.id == value)
                    ?.he ||
                  workTypes?.find((workType: DictType) => workType?.id == value)
                    ?.en ||
                  '---'
                : workTypes?.find((workType: DictType) => workType?.id == value)
                    ?.en ||
                  workTypes?.find((workType: DictType) => workType?.id == value)
                    ?.he ||
                  '---'
              : translate('Select Type')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[250px] p-0'>
          <Command className={isHebrew ? 'rtl' : 'ltr'}>
            <CommandInput placeholder={`${translate('Search')}...`} />
            <CommandList>
              <CommandEmpty>{translate('No items found')}.</CommandEmpty>
              <CommandGroup>
                {workTypes?.map((option: any, index: number) => (
                  <ProjectWorkTypeDictionaryEditor
                    id={option?.id}
                    key={index}
                    value={option}
                    refetch={refetchWorkTypes}
                  >
                    <CommandItem
                      key={option.id}
                      value={
                        isHebrew
                          ? option.he
                            ? option?.he
                            : option?.en
                          : option?.en
                      }
                      onSelect={(option: any) => {
                        setValue(workType?.id);
                        setOpen(false);

                        const workTypeId = workTypes?.find(
                          (workType: DictType) =>
                            workType?.he == option || workType?.en == option,
                        )?.id;

                        updateProjectWorkTypeMutation.mutate(workTypeId);
                      }}
                    >
                      {isHebrew
                        ? option.he
                          ? option?.he
                          : option?.en || '---'
                        : option?.en || '---'}
                    </CommandItem>
                  </ProjectWorkTypeDictionaryEditor>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className='p-2'>
            <AddWorkTypeForm refetch={refetchWorkTypes} />
          </div>{' '}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full h-8 text-black text-left flex justify-start capitalize'
        >
          {value
            ? isHebrew
              ? workTypes?.find((workType: DictType) => workType?.id == value)
                  ?.he
              : workTypes?.find((workType: DictType) => workType?.id == value)
                  ?.en
            : translate('Select Type')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command className={isHebrew ? 'rtl' : 'ltr'}>
          <CommandInput placeholder={`${translate('Search')}...`} />
          <CommandList>
            <CommandEmpty>{translate('No items found')}.</CommandEmpty>
            <CommandGroup>
              {workTypes?.map((workType: DictType) => (
                <CommandItem
                  key={workType.id}
                  value={
                    isHebrew
                      ? workType.he
                        ? workType?.he
                        : workType?.en
                      : workType?.en
                  }
                  onSelect={(currentValue: any) => {
                    setValue(workType?.id);
                    setOpen(false);
                    setWorkType && setWorkType(workType?.id);
                  }}
                  className={cn('my-1 ')}
                >
                  {isHebrew
                    ? workType.he
                      ? workType?.he
                      : workType?.en
                    : workType?.en}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className='px-2 pb-2'>
          <Separator />
          <AddWorkTypeForm refetch={refetchWorkTypes} />
        </div>{' '}
      </PopoverContent>
    </Popover>
  );
};

export default ProjectWorkTypeEditor;
